import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, TrendingUp, Inbox, CheckCircle2, Banknote, Phone, Mail } from "lucide-react";
import { api, TOKEN_KEY } from "@/lib/apiClient";
import { toast } from "sonner";

const STATUS_OPTIONS = ["new", "contacted", "approved", "funded", "rejected"];
const STATUS_COLOR = {
  new: "text-blue-400 border-blue-400/30 bg-blue-400/5",
  contacted: "text-amber-400 border-amber-400/30 bg-amber-400/5",
  approved: "text-emerald-400 border-emerald-400/30 bg-emerald-400/5",
  funded: "text-gold border-gold/40 bg-gold/5",
  rejected: "text-red-400 border-red-400/30 bg-red-400/5",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/login", { replace: true });
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const [meR, leadsR, statsR] = await Promise.all([
        api.get("/admin/me"),
        api.get("/admin/leads"),
        api.get("/admin/leads/stats"),
      ]);
      setMe(meR.data);
      setLeads(leadsR.data);
      setStats(statsR.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login", { replace: true });
      } else {
        toast.error("Failed to load dashboard data.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Lead marked as ${status}.`);
      const s = await api.get("/admin/leads/stats");
      setStats(s.data);
    } catch {
      toast.error("Failed to update lead.");
    }
  };

  const filtered = useMemo(
    () => (filter === "all" ? leads : leads.filter((l) => l.status === filter)),
    [leads, filter]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center text-white/60">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#050B14] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 bg-[#050B14]/90 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 border border-gold flex items-center justify-center">
                <div className="w-1.5 h-1.5 bg-gold rotate-45" />
              </div>
              <span className="font-heading text-base">Direct<span className="text-gold">Fund</span>Cap</span>
            </Link>
            <span className="text-xs tracking-[0.2em] uppercase text-white/40">Admin · Control Room</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="text-white/60 hover:text-gold flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
            <span className="hidden md:block text-sm text-white/50">{me?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="text-white/60 hover:text-red-400 flex items-center gap-2 text-sm border border-white/10 px-3 py-1.5"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight">Funding Pipeline</h1>
          <p className="text-white/50 text-sm mt-1">All inbound applications from directfundcap.com</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-10">
          <Stat icon={Inbox} label="Total Leads" value={stats?.total ?? 0} />
          <Stat icon={TrendingUp} label="Last 7 Days" value={stats?.last_7_days ?? 0} accent />
          <Stat icon={Phone} label="Contacted" value={stats?.by_status?.contacted ?? 0} />
          <Stat icon={CheckCircle2} label="Approved" value={stats?.by_status?.approved ?? 0} />
          <Stat icon={Banknote} label="Funded" value={stats?.by_status?.funded ?? 0} />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {[{ k: "all", label: "All" }, ...STATUS_OPTIONS.map((s) => ({ k: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))].map((opt) => (
            <button
              key={opt.k}
              data-testid={`filter-${opt.k}`}
              onClick={() => setFilter(opt.k)}
              className={`text-xs tracking-wider uppercase px-3 py-1.5 border transition-colors ${
                filter === opt.k ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/60 hover:border-white/30"
              }`}
            >
              {opt.label}
            </button>
          ))}
          <span className="text-xs text-white/40 ml-auto">{filtered.length} result{filtered.length === 1 ? "" : "s"}</span>
        </div>

        {/* Leads Table */}
        <div className="border border-white/10 overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-white/[0.03] text-white/50 text-xs tracking-wider uppercase">
                <th className="text-left px-4 py-3 font-medium">Business</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-left px-4 py-3 font-medium">Revenue</th>
                <th className="text-left px-4 py-3 font-medium">Time in Biz</th>
                <th className="text-left px-4 py-3 font-medium">Requested</th>
                <th className="text-left px-4 py-3 font-medium">Industry</th>
                <th className="text-left px-4 py-3 font-medium">Submitted</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-white/40 py-16" data-testid="leads-empty">
                    No leads yet. Submissions from the website will appear here in real time.
                  </td>
                </tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-4">
                    <div className="text-white font-medium">{l.business_name}</div>
                    <div className="text-white/40 text-xs">{l.notes || "—"}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-white">{l.contact_name}</div>
                    <div className="text-white/50 text-xs flex items-center gap-2 mt-1">
                      <Mail className="w-3 h-3" /> {l.email}
                    </div>
                    <div className="text-white/50 text-xs flex items-center gap-2">
                      <Phone className="w-3 h-3" /> {l.phone}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-white/80">{l.monthly_revenue}</td>
                  <td className="px-4 py-4 text-white/80">{l.time_in_business}</td>
                  <td className="px-4 py-4 text-gold font-semibold">{l.requested_amount}</td>
                  <td className="px-4 py-4 text-white/60">{l.industry || "—"}</td>
                  <td className="px-4 py-4 text-white/50 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <select
                      value={l.status}
                      onChange={(e) => updateStatus(l.id, e.target.value)}
                      data-testid={`lead-status-${l.id}`}
                      className={`text-xs uppercase tracking-wider px-2 py-1 border bg-transparent ${STATUS_COLOR[l.status]}`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s} className="bg-[#0A1326] text-white">
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-5 ${accent ? "bg-[#0A1326]" : "bg-[#050B14]"}`}>
      <div className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-white/50 mb-3">
        <Icon className="w-3.5 h-3.5 text-gold" /> {label}
      </div>
      <div className="font-heading text-3xl text-white tracking-tighter">{value}</div>
    </div>
  );
}

